import { doesElementContainsElement, getFocusableElements } from "@utils/general";
import React from "react";
import { KeyName } from "../../keyName";

interface IDialogKeyboardArgs {
    ref: HTMLElement;
    onConfirm?: () => void;
}

const getDialogsFocusableElements = (args: IDialogKeyboardArgs) => {
    // we can't store getFocusableElements results in private variable
    // becasue elements rendered in dialog can change during its lifetime!
    if (!args.ref) {
        return [];
    }
    const elements = getFocusableElements(args.ref);
    return Array.from(elements);
};


// in case the focus got somehow out of the dialog (or on the dialog parent element)
// we need to trap it back inside the dialog
const isElementInElements = (element: HTMLElement, elements: Element[]) => {
    return elements.includes(element);
};

const focusFocusable = (index: number, args: IDialogKeyboardArgs) => {
    const focusableElements = getDialogsFocusableElements(args);

    (focusableElements[index] as HTMLElement)?.focus();
};

const handleTabForward = (event: React.KeyboardEvent, args: IDialogKeyboardArgs) => {
    const focusableElements = getDialogsFocusableElements(args);

    if (document.activeElement === focusableElements[focusableElements.length - 1]
        || !isElementInElements(document.activeElement as HTMLElement, focusableElements)) {
        event.preventDefault();
        focusFocusable(0, args);
    }
};

const handleTabBackward = (event: React.KeyboardEvent, args: IDialogKeyboardArgs) => {
    const focusableElements = getDialogsFocusableElements(args);

    if (document.activeElement === focusableElements[0]
        || !isElementInElements(document.activeElement as HTMLElement, focusableElements)) {
        event.preventDefault();
        focusFocusable(focusableElements.length - 1, args);
    }
};

export const setupKeysAfterDialogOpen = (args: IDialogKeyboardArgs) => {
    // wait for items to be rendered (e.g. SmartField is kinda async)
    const focusableElements = getDialogsFocusableElements(args);

    // only auto focus on input
    if (focusableElements[0]?.tagName === "INPUT") {
        focusFocusable(0, args);
    } else { // otherwise, focus the whole dialog, so that the esc key works for closing
        args.ref?.focus();
    }
};


export const handleDialogKeyDown = (event: React.KeyboardEvent, args: IDialogKeyboardArgs) => {
    if (event.defaultPrevented) {
        // Do not trigger action if it's handled inside the dialog
        return;
    }
    switch (event.key) {
        case KeyName.Enter: {
            event.stopPropagation();
            args.onConfirm?.();
            break;
        }

        case KeyName.Tab: {
            // only handle focus inside dialog,
            // it can be outside, e.g. in date picker
            if (doesElementContainsElement(args.ref, document.activeElement) || args.ref === document.activeElement || document.body === document.activeElement) {
                event.stopPropagation();
                // prevent focus from getting outside of dialog
                if (event.shiftKey) {
                    handleTabBackward(event, args);
                } else {
                    handleTabForward(event, args);
                }
            }
            break;
        }
    }
};
